import React, { FC, useEffect, useState } from "react";
import {
  Container,
  InputWrapper,
  ButtonWrapper,
} from './styles'
import { billingService } from "../../../../api";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, TopProgress, Typography } from "../../../../components";
import Toggle from "../../../../components/toggle";
import { useTranslation } from "react-i18next";
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useUi } from "../../../../context/ui.context";

const GatewayEdit:FC = () => {
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
  const [capabilities, setCapabilities] = useState<any>({});

  const navigate = useNavigate()
  const {gatewayId} = useParams()
  const [t] = useTranslation()
  const {viewAlert} = useUi()

  const [initialValues, setInitialValues] = useState<any>({
    enabled: false,
    visible_to_user: false,
    recurring_payments_enabled: false,
    display_name: '',
    instructions: '',
    fixed_fee: 0,
    percent_fee: 0,
  })

  const getData = async () => {
    if(!gatewayId) return;
    try{
      const res = await billingService.getGatewayById(gatewayId)
      const data = res.data;
      setInitialValues({
        enabled: data.enabled || false,
        visible_to_user: data.visible_to_user || false,
        recurring_payments_enabled: data.recurring_payments_enabled || false,
        display_name: data.display_name || '',
        instructions: data.instructions || '',
        fixed_fee: data.fixed_fee || 0,
        percent_fee: data.percent_fee || 0,
      });
      setCapabilities(data.module_settings?.capabilities || {});
      setPageLoading(false);
    } catch(err:any) {
      console.log('err', err);
    }
  }

  useEffect(() => {
    getData()
  },[])

  const onSubmit = async (values:any) => {
    if(!gatewayId) return;
    setLoading(true)
    try{
      const payload:any = {
        enabled: values.enabled,
        visible_to_user: values.visible_to_user,
        recurring_payments_enabled: values.recurring_payments_enabled,
        display_name: values.display_name,
        instructions: values.instructions,
        fixed_fee: values.fixed_fee,
        percent_fee: values.percent_fee,
      }
      await billingService.editGatewayById(gatewayId, payload)
      setLoadingComplete(true)
      viewAlert({
        severity:'success',
        message: t("GATEWAY_SAVED"),
      })
      setTimeout(() => {
        setLoading(false)
        setLoadingComplete(false)
        navigate(-1)
      },1000)
    } catch(err:any){
      setLoading(false)
      if(err?.response?.data){
        setErrors(err.response.data)
      }
      console.log('err', err);
    }
  }

  const {
    values,
    errors,
    touched,
    setErrors,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      display_name: yup.string().required(),
    }),
    initialValues,
    onSubmit,
  });

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  if(pageLoading) return <TopProgress loading={pageLoading}/>

  return(
    <Container>
      <Typography variant="h3">{t("EDIT_GATEWAY")}</Typography>
      <div style={{display:'flex', gap:'20px', flexWrap:'wrap'}}>
        <Toggle
          title={t("ENABLED")}
          value={values.enabled}
          onChange={() => setFieldValue('enabled', !values.enabled)}
        />
        {capabilities.can_process_payments && (
          <Toggle
            title={t("VISIBLE_TO_USER")}
            value={values.visible_to_user}
            onChange={() => setFieldValue('visible_to_user', !values.visible_to_user)}
          />
        )}
        {capabilities.supports_recurring_payments && (
          <Toggle
            title={t("ENABLE_RECURRING_PAYMENTS")}
            value={values.recurring_payments_enabled}
            onChange={() => setFieldValue('recurring_payments_enabled', !values.recurring_payments_enabled)}
          />
        )}
      </div>
      <InputWrapper>
        <Input
          required
          title={t('DISPLAY_NAME')}
          fieldName='display_name'
          placeholder={''}
          onBlur={handleBlur}
          {...formikProps}
        />
        {values.recurring_payments_enabled && (
          <Input
            title={t("INSTRUCTIONS")}
            fieldName='instructions'
            placeholder={t("RECURRING_INSTRUCTIONS_PLACEHOLDER")}
            onBlur={handleBlur}
            {...formikProps}
          />
        )}
        {!capabilities.returns_fee_information ? (
          <>
            <Typography variant="body">
              {t("GATEWAY_NO_FEE_INFO")}
            </Typography>
            <Input
              title={t("FIXED_FEE")}
              fieldName='fixed_fee'
              placeholder={''}
              type='number'
              onBlur={handleBlur}
              {...formikProps}
            />
            <Input
              title={t("PERCENT_FEE")}
              fieldName='percent_fee'
              placeholder={''}
              type='number'
              onBlur={handleBlur}
              {...formikProps}
            />
          </>
        ) : (
          <Typography variant="body">
            {t("GATEWAY_HAS_FEE_INFO")}
          </Typography>
        )}
      </InputWrapper>
      <ButtonWrapper>
        <Button
          variant="primary"
          size="display"
          onClick={handleSubmit}
          loading={loading}
          loadingComplete={loadingComplete}
          title={t("SAVE")}
        />
        <Button
          variant="stroke"
          size="display"
          onClick={() => navigate(-1)}
          title={t("CANCEL")}
        />
      </ButtonWrapper>
    </Container>
  )
}

export default GatewayEdit
